/**
 * Client Delivery Ledger
 * 
 * Pure accounting logic for client entitlements, delivery tracking, credits, and adjustments.
 * This is the single source of truth for what clients bought, received, and are owed.
 * 
 * CRITICAL PRINCIPLES:
 * - This is accounting logic, NOT routing logic
 * - Must never influence routing directly
 * - Must be fully deterministic and auditable
 * - Must be derived from existing delivery + contract data
 * - Must be safe to show to clients in the future
 * - All functions are pure and side-effect free
 */

import type { Contract, ContractType } from '@/contexts/ContractsContext';
import type { LifecycleLead } from '@/lib/leadStateMachine';

// --- Types ---

export type LedgerDeliveryStatus = 
  | 'ON_TRACK'
  | 'UNDER_DELIVERED'
  | 'OVER_DELIVERED'
  | 'COMPLETE';

export interface LeadDeliveryBreakdown {
  delivered: number;
  pending: number;
  retrying: number;
  failed: number;
}

export interface LedgerSnapshot {
  // Client identification
  clientId: string;
  clientName: string;
  
  // Core entitlement numbers
  totalPurchased: number;
  totalDelivered: number;
  totalPending: number;
  totalRetries: number;
  totalFailed: number;
  
  // Credits and adjustments
  totalCredited: number;
  rolloversReceived: number;
  rolloversGiven: number;
  
  // Calculated values
  remainingEntitlement: number;
  overDelivered: number;
  underDelivered: number;
  
  // Status
  deliveryStatus: LedgerDeliveryStatus;
  deliveryProgressPercent: number;
  
  // Contract details
  activeContracts: ContractSummary[];
  completedContracts: ContractSummary[];
  
  // Timing
  evaluatedAt: number;
  oldestContractStart: number | null;
  newestContractEnd: number | null;
  
  // Data quality
  dataWarnings: DataWarning[];
}

export interface ContractSummary {
  contractId: string;
  contractType: ContractType;
  totalCommitted: number;
  delivered: number;
  remainingDays: number;
  progressPercent: number;
  status: string;
}

export interface DataWarning {
  code: string;
  message: string;
  severity: 'info' | 'warn' | 'error';
  details?: Record<string, unknown>;
}

export interface LedgerComputeResult {
  snapshots: Map<string, LedgerSnapshot>;
  summary: LedgerSummary;
  warnings: DataWarning[];
  computedAt: number;
}

export interface LedgerSummary {
  totalClients: number;
  onTrackClients: number;
  underDeliveredClients: number;
  overDeliveredClients: number;
  completeClients: number;
  totalPurchasedAcrossAll: number;
  totalDeliveredAcrossAll: number;
  systemHealthScore: number; // 0-100
}

// --- Helper Functions ---

/**
 * Clamp a value between min and max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Calculate days remaining until a timestamp
 */
function daysUntil(timestamp: number, now: number): number {
  const msRemaining = timestamp - now;
  return Math.max(0, Math.ceil(msRemaining / (24 * 60 * 60 * 1000)));
}

/**
 * Calculate progress percentage safely
 */
function safeProgressPercent(delivered: number, total: number): number {
  if (total <= 0) return 100;
  return clamp((delivered / total) * 100, 0, 100);
}

// --- Core Ledger Functions ---

/**
 * Compute lead delivery breakdown for a specific client/agent
 */
export function computeLeadDeliveryBreakdown(
  leads: LifecycleLead[],
  clientId: string,
  contractIds?: Set<string>
): LeadDeliveryBreakdown {
  let delivered = 0;
  let pending = 0;
  let retrying = 0;
  let failed = 0;

  for (const lead of leads) {
    // Check if this lead was assigned to this client
    if (lead.assignedAgentId !== clientId) continue;
    if (contractIds) {
      if (!lead.contractId) continue;
      if (!contractIds.has(lead.contractId)) continue;
    }

    switch (lead.status) {
      case 'ROUTED':
        delivered++;
        break;
      case 'ROUTING_IN_PROGRESS':
        pending++;
        break;
      case 'RETRY_QUEUED':
        retrying++;
        break;
      case 'UNSOLD':
      case 'STOCKED':
      case 'ARCHIVED':
        // These were attempted but not successfully delivered
        // Only count if they were assigned to this agent at some point
        failed++;
        break;
      case 'CREDITED':
        // Credited leads were delivered then reversed — don't count toward delivery
        // The contract counter was already decremented by the credit handler
        break;
    }
  }

  return { delivered, pending, retrying, failed };
}

/**
 * Compute ledger snapshot for a single client
 */
export function computeClientLedgerSnapshot(
  clientId: string,
  clientName: string,
  contracts: Contract[],
  leads: LifecycleLead[],
  now: number = Date.now(),
  clientEmail?: string
): LedgerSnapshot {
  const warnings: DataWarning[] = [];

  // Filter contracts for this client — match by agentId OR agentEmail
  // This handles webhook-created contracts where agent_id is null but agent_email is set
  // Orphan IDs (orphan:contract-xxx) match by contract ID directly
  const clientContracts = contracts.filter(c => {
    if (clientId.startsWith('orphan:') && c.id === clientId.slice(7)) return true;
    if (c.agentId === clientId) return true;
    if (clientEmail && c.agentEmail === clientEmail) return true;
    return false;
  });
  const activeContracts = clientContracts.filter(c => c.status === 'active' || c.status === 'at_risk');
  const _completedContracts = clientContracts.filter(c => c.status === 'fulfilled');
  
  // Calculate totals from contracts
  let totalPurchased = 0;
  let contractDelivered = 0;
  let oldestContractStart: number | null = null;
  let newestContractEnd: number | null = null;
  
  const activeContractSummaries: ContractSummary[] = [];
  const completedContractSummaries: ContractSummary[] = [];
  
  for (const contract of clientContracts) {
    totalPurchased += contract.totalLeadsCommitted;
    contractDelivered += contract.leadsDelivered;
    
    // Track date range
    if (oldestContractStart === null || contract.cycleStart < oldestContractStart) {
      oldestContractStart = contract.cycleStart;
    }
    if (newestContractEnd === null || contract.cycleEnd > newestContractEnd) {
      newestContractEnd = contract.cycleEnd;
    }
    
    const summary: ContractSummary = {
      contractId: contract.id,
      contractType: contract.contractType,
      totalCommitted: contract.totalLeadsCommitted,
      delivered: contract.leadsDelivered,
      remainingDays: daysUntil(contract.cycleEnd, now),
      progressPercent: safeProgressPercent(contract.leadsDelivered, contract.totalLeadsCommitted),
      status: contract.status,
    };
    
    if (contract.status === 'active' || contract.status === 'at_risk') {
      activeContractSummaries.push(summary);
    } else if (contract.status === 'fulfilled') {
      completedContractSummaries.push(summary);
    }
  }
  
  // Calculate lead-based delivery numbers scoped to this client's contracts
  const contractIds = new Set(clientContracts.map(c => c.id));
  const leadBreakdown = computeLeadDeliveryBreakdown(leads, clientId, contractIds);
  
  // Leads table is ground truth for delivery counts.
  // Only fall back to contract counter if leads table has actual deliveries.
  // This prevents "ghost values" where contract counter is stale/wrong.
  let totalDelivered: number;
  if (contractDelivered > 0 && leadBreakdown.delivered === 0) {
    // Ghost value detected — contract counter is stale
    totalDelivered = 0;
    warnings.push({
      code: 'GHOST_DELIVERY_COUNT',
      message: `Contract counter shows ${contractDelivered} delivered but leads table shows 0. Counter is stale — using 0.`,
      severity: 'error',
      details: { contractDelivered, leadTableDelivered: 0 },
    });
  } else if (leadBreakdown.delivered > 0) {
    totalDelivered = leadBreakdown.delivered;
    if (contractDelivered !== leadBreakdown.delivered) {
      warnings.push({
        code: 'DELIVERY_COUNT_MISMATCH',
        message: `Contract shows ${contractDelivered} delivered, leads table shows ${leadBreakdown.delivered} (contract_id scoped)`,
        severity: 'warn',
        details: { contractCount: contractDelivered, leadCount: leadBreakdown.delivered },
      });
    }
  } else {
    totalDelivered = contractDelivered;
  }
  
  // Calculate pending and retries
  const totalPending = leadBreakdown.pending;
  const totalRetries = leadBreakdown.retrying;
  const totalFailed = leadBreakdown.failed;
  
  // Credits (placeholder) and rollovers (from contract data)
  const totalCredited = 0;
  const rolloversReceived = clientContracts.reduce((sum, c) => sum + (c.rolloverLeads ?? 0), 0);
  const rolloversGiven = 0;
  
  // Calculate remaining entitlement
  const remainingEntitlement = Math.max(0, totalPurchased - totalDelivered - totalPending - totalRetries);
  
  // Calculate over/under delivery
  const expectedTotal = totalPurchased + rolloversReceived - rolloversGiven + totalCredited;
  const overDelivered = Math.max(0, totalDelivered - expectedTotal);
  const underDelivered = Math.max(0, expectedTotal - totalDelivered);
  
  // Determine delivery status
  let deliveryStatus: LedgerDeliveryStatus;
  
  if (activeContracts.length === 0 && totalDelivered >= totalPurchased) {
    deliveryStatus = 'COMPLETE';
  } else if (overDelivered > 0) {
    deliveryStatus = 'OVER_DELIVERED';
  } else if (underDelivered > totalPurchased * 0.1) {
    // More than 10% under-delivered
    deliveryStatus = 'UNDER_DELIVERED';
  } else {
    deliveryStatus = 'ON_TRACK';
  }
  
  // Calculate overall progress
  const deliveryProgressPercent = safeProgressPercent(totalDelivered, totalPurchased);
  
  // Add warning if no contracts found
  if (clientContracts.length === 0) {
    warnings.push({
      code: 'NO_CONTRACTS',
      message: 'No contracts found for this client',
      severity: 'info',
    });
  }
  
  return {
    clientId,
    clientName,
    totalPurchased,
    totalDelivered,
    totalPending,
    totalRetries,
    totalFailed,
    totalCredited,
    rolloversReceived,
    rolloversGiven,
    remainingEntitlement,
    overDelivered,
    underDelivered,
    deliveryStatus,
    deliveryProgressPercent,
    activeContracts: activeContractSummaries,
    completedContracts: completedContractSummaries,
    evaluatedAt: now,
    oldestContractStart,
    newestContractEnd,
    dataWarnings: warnings,
  };
}

/**
 * Compute ledger snapshots for all clients with contracts
 */
export function computeAllClientLedgers(
  contracts: Contract[],
  leads: LifecycleLead[],
  now: number = Date.now()
): LedgerComputeResult {
  const snapshots = new Map<string, LedgerSnapshot>();
  const allWarnings: DataWarning[] = [];
  
  // Get unique clients from contracts — group by agentId, but also resolve
  // email-only contracts (where agentId is empty but agentEmail is set) to
  // an existing client with matching email when possible.
  const clientIds = new Set<string>();
  const clientNames = new Map<string, string>();
  const clientEmails = new Map<string, string>();

  // First pass: collect all agents with real IDs and their emails
  for (const contract of contracts) {
    if (contract.agentId) {
      clientIds.add(contract.agentId);
      clientNames.set(contract.agentId, contract.agentName);
      if (contract.agentEmail) {
        clientEmails.set(contract.agentId, contract.agentEmail);
      }
    }
  }

  // Second pass: for contracts with empty agentId, try to resolve by email
  for (const contract of contracts) {
    if (!contract.agentId && contract.agentEmail) {
      // Look for an existing client with the same email
      let resolved = false;
      for (const [_existingId, existingEmail] of clientEmails) {
        if (existingEmail === contract.agentEmail) {
          // This contract belongs to the existing client
          resolved = true;
          break;
        }
      }
      if (!resolved) {
        // No matching client found — create entry keyed by email
        const syntheticId = `email:${contract.agentEmail}`;
        clientIds.add(syntheticId);
        clientNames.set(syntheticId, contract.agentName || contract.agentEmail);
        clientEmails.set(syntheticId, contract.agentEmail);
      }
    } else if (!contract.agentId && !contract.agentEmail) {
      // Orphaned contract with no identifying info — still show it so admin can investigate
      const orphanId = `orphan:${contract.id}`;
      clientIds.add(orphanId);
      clientNames.set(orphanId, contract.agentName || `Unlinked Contract`);
    }
  }

  // Compute snapshot for each client
  let onTrackCount = 0;
  let underDeliveredCount = 0;
  let overDeliveredCount = 0;
  let completeCount = 0;
  let totalPurchasedSum = 0;
  let totalDeliveredSum = 0;

  for (const clientId of clientIds) {
    const clientName = clientNames.get(clientId) || 'Unknown';
    const clientEmail = clientEmails.get(clientId);
    const snapshot = computeClientLedgerSnapshot(clientId, clientName, contracts, leads, now, clientEmail);
    
    snapshots.set(clientId, snapshot);
    
    // Aggregate warnings
    allWarnings.push(...snapshot.dataWarnings);
    
    // Update summary counts
    totalPurchasedSum += snapshot.totalPurchased;
    totalDeliveredSum += snapshot.totalDelivered;
    
    switch (snapshot.deliveryStatus) {
      case 'ON_TRACK':
        onTrackCount++;
        break;
      case 'UNDER_DELIVERED':
        underDeliveredCount++;
        break;
      case 'OVER_DELIVERED':
        overDeliveredCount++;
        break;
      case 'COMPLETE':
        completeCount++;
        break;
    }
  }
  
  // Calculate system health score
  // Based on: % of clients on track or complete
  const totalClients = clientIds.size;
  const healthyClients = onTrackCount + completeCount;
  const systemHealthScore = totalClients > 0
    ? Math.round((healthyClients / totalClients) * 100)
    : 100;
  
  return {
    snapshots,
    summary: {
      totalClients,
      onTrackClients: onTrackCount,
      underDeliveredClients: underDeliveredCount,
      overDeliveredClients: overDeliveredCount,
      completeClients: completeCount,
      totalPurchasedAcrossAll: totalPurchasedSum,
      totalDeliveredAcrossAll: totalDeliveredSum,
      systemHealthScore,
    },
    warnings: allWarnings,
    computedAt: now,
  };
}

// --- Query Helpers ---

/**
 * Get clients that are at risk (under-delivered)
 */
export function getAtRiskClients(result: LedgerComputeResult): LedgerSnapshot[] {
  return Array.from(result.snapshots.values())
    .filter(s => s.deliveryStatus === 'UNDER_DELIVERED');
}

/**
 * Get clients that have been over-delivered
 */
export function getOverDeliveredClients(result: LedgerComputeResult): LedgerSnapshot[] {
  return Array.from(result.snapshots.values())
    .filter(s => s.deliveryStatus === 'OVER_DELIVERED');
}

/**
 * Get clients with active entitlements remaining
 */
export function getClientsWithEntitlements(result: LedgerComputeResult): LedgerSnapshot[] {
  return Array.from(result.snapshots.values())
    .filter(s => s.remainingEntitlement > 0);
}

/**
 * Get clients with data consistency warnings
 */
export function getClientsWithWarnings(result: LedgerComputeResult): LedgerSnapshot[] {
  return Array.from(result.snapshots.values())
    .filter(s => s.dataWarnings.length > 0);
}

/**
 * Validate ledger against other system data sources
 * Returns warnings if discrepancies are found
 */
export function validateLedgerConsistency(
  ledgerResult: LedgerComputeResult,
  reconciliationTotalDelivered?: Map<string, number>,
  contractsPageTotalDelivered?: Map<string, number>
): DataWarning[] {
  const warnings: DataWarning[] = [];
  
  for (const [clientId, snapshot] of ledgerResult.snapshots) {
    // Check against reconciliation data if available
    if (reconciliationTotalDelivered?.has(clientId)) {
      const reconValue = reconciliationTotalDelivered.get(clientId)!;
      if (reconValue !== snapshot.totalDelivered) {
        warnings.push({
          code: 'RECONCILIATION_MISMATCH',
          message: `Client ${snapshot.clientName}: Ledger shows ${snapshot.totalDelivered}, Reconciliation shows ${reconValue}`,
          severity: 'error',
          details: { clientId, ledgerValue: snapshot.totalDelivered, reconciliationValue: reconValue },
        });
      }
    }
    
    // Check against contracts page data if available
    if (contractsPageTotalDelivered?.has(clientId)) {
      const contractsValue = contractsPageTotalDelivered.get(clientId)!;
      if (contractsValue !== snapshot.totalDelivered) {
        warnings.push({
          code: 'CONTRACTS_PAGE_MISMATCH',
          message: `Client ${snapshot.clientName}: Ledger shows ${snapshot.totalDelivered}, Contracts page shows ${contractsValue}`,
          severity: 'warn',
          details: { clientId, ledgerValue: snapshot.totalDelivered, contractsValue },
        });
      }
    }
  }
  
  return warnings;
}
